import { Injectable } from '@angular/core';
import { FieldExpression, term } from 'lucene';

import { TranslationService } from '../services/translation.service';
import {
	DatacenterDateFilter,
	FilterType,
	DatacenterFreeTextFilter,
	SuggestiveListFilter,
	SuggestiveListWithIdFilter,
	DatacenterNumericFilter,
	OrderNumberFilter,
	FilterApiRequest,
	DropdownFilter,
	DatacenterBooleanFilter,
	GlobalSearchFilter,
	DocumentDateFilter
} from '../models/datacenter-filters';
import { CLAIM_SERVER_URL, ORDER_SERVER_URL, AQDB_SERVER_URL, AUTH_SERVER_URL } from '../../config';
import { DatacenterType } from '../models/datacenter-aggrid-view';
import { UserService } from '../services/user.service';
import { User } from '../models/user';
import {
	ProductCharacteristicsTranslationService
} from '../services/product-characteristics-translation.services';

import { DatacenterDefaultConfiguration } from './datacenter-default-configuration';
import { ColumnType } from './column-type';
import { BatchFiltersConfiguration } from './types/batches/batch-filter-configuration';
import { OrderFiltersConfiguration } from './types/orders/order-filter-configuration';
import { ShipmentFiltersConfiguration } from './types/shipments/shipment-filter-configuration';
import { PrototypeFiltersConfiguration } from './types/prototypes/prototype-filter-configuration';
import { DocumentFiltersConfiguration } from './types/commercial-documents/document-filter-configuration';
import { ScheduleBusinessOrderFiltersConfiguration }
	from './types/schedule-business-orders/schedule-business-order-filter-configuration';
import { ProductListFiltersConfiguration } from './types/product-list/product-list-filter-configuration';

const GET_CLAIM_URL = `${ CLAIM_SERVER_URL }/v2/qualityIssues`;
const GET_ORG_URL = `${ AUTH_SERVER_URL }/v2/organizations:external`;
const GET_DATACENTER_ORDERS_URL = `${ ORDER_SERVER_URL }/v1/calledBatches:mills`;
const GET_DATACENTER_BATCHES_URL = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;
const GET_AQDB_SERVER_URL = `${ AQDB_SERVER_URL }/v2/qualityData`;
const GET_COUNTRIES = `${ AUTH_SERVER_URL }/v2/countries`;
let GET_SERVICES = `${ AUTH_SERVER_URL }/v2/organizations:external`;

/**
 * 'userValidationStatus' is a frontend-only code: it never reaches the backend, it is translated
 * to personStatus:"pend" AND status:"activ". Other statuses only match the status field.
 * @param _translation - Unused translation label of the selected option.
 * @param code - Selected status code.
 * @returns The lucene expression targeting the relevant status field(s).
 */
const createUsersStatusFilterExpression = (_translation?: string, code?: string) => {
	if (code !== 'userValidationStatus') {
		return {
			field: 'status',
			term: `"${ term.escape(code ?? '') }"`,
			quoted: false
		};
	}
	return {
		left: {
			field: 'personStatus',
			term: '"pend"',
			quoted: false
		},
		operator: <const> 'AND',
		right: {
			field: 'status',
			term: '"activ"',
			quoted: false
		},
		parenthesized: true
	};
};

type FilterTypeFn = () => FilterType;

class FiltersConfiguration {
	user: User;
	organizationId: string;

	constructor(
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly translationService: TranslationService,
		private readonly userService: UserService
	) {
		this.user = this.userService.user;
		this.organizationId = String(this.user.currentVisibility.organizationId);
		GET_SERVICES = `${ GET_SERVICES }/${ this.organizationId }/services`;
	}

	columnsToFilters(
		columns: ReturnType<(typeof this.defaultConfiguration)['getAllColumnsOfDatacenter']>
	): Map<string, FilterTypeFn> {
		const map = new Map<string, FilterTypeFn>();
		for (const column of columns) {
			if (!column.filter || !column.columnType || !column.colId || !column.headerName) {
				continue;
			}
			const filterId = column.colId;
			const label = column.headerName;
			// Derive the API field name from the column definition so generated filters
			// emit a valid Lucene field expression instead of a field-less clause.
			const apiName = column.field ?? column.fields?.[0] ?? column.colId;
			switch (column.columnType) {
			case ColumnType.DATE_COLUMN:
				map.set(column.colId, () => new DatacenterDateFilter(
					filterId,
					label,
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					apiName,
					'date'
				));
				break;
			case ColumnType.NUMBER_COLUMN:
				map.set(column.colId, () => new DatacenterNumericFilter(
					filterId,
					label,
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					apiName
				));
				break;
			case ColumnType.ORDER_NUMBER_COLUMN: {
				const referenceName = column.fields?.[0] ?? apiName;
				const itemName = column.fields?.[1] ?? referenceName;
				map.set(column.colId, () => new OrderNumberFilter(
					filterId,
					label,
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					referenceName,
					itemName
				));
				break;
			}
			// case ColumnType.DIMENSION_COLUMN:
			default:
				map.set(column.colId, () => new DatacenterFreeTextFilter(
					filterId,
					label,
					label,
					apiName
				));
			}
		}
		return map;
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	CALLOFF_BY_BATCH_FILTERS = () => {
		const map = this.columnsToFilters(this.defaultConfiguration.getAllColumnsOfDatacenter('calloff-by-batch'));

		// Override cdd with week format so the Lucene filter uses ISO week notation (e.g. 2026-W23)
		map.set('cdd', () => new DatacenterDateFilter(
			'cdd',
			this.translationService.getTR('filter.cdd'),
			this.translationService.getTR('filter.date.from'),
			this.translationService.getTR('filter.date.to'),
			'cddWeek',
			'week'
		));

		map.set('soldTo', () => new SuggestiveListFilter(
			'soldTo',
			this.translationService.getTR('order.customer'),
			this.translationService.getTR('order.customer'),
			'soldTo',
			'soldToDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500, distinct: true })
		));

		map.set('shipTo', () => new SuggestiveListWithIdFilter(
			'shipTo',
			this.translationService.getTR('order.ship.to'),
			this.translationService.getTR('order.ship.to'),
			'shipTo',
			'shipToDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500, distinct: true }),
			undefined,
			'shipToAddress'
		));

		map.set('customerArticleReference', () => new SuggestiveListWithIdFilter(
			'customerArticleReference',
			this.translationService.getTR('datacenter-filter.customer-article-reference'),
			this.translationService.getTR('datacenter-filter.customer-article-reference'),
			'customerArticleReference',
			'customerArticleReference',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500, distinct: true }),
			[],
			undefined,
			this.translationService.getTR('filter.no-value')
		));

		map.set('coating', () => new DropdownFilter(
			'coating',
			this.translationService.getTR('datacenter-filter.coating'),
			this.translationService.getTR('datacenter-filter.coating'),
			'coating',
			'coatingDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500 })
		));

		map.set('deliveryMode', () => new DropdownFilter(
			'deliveryMode',
			this.translationService.getTR('filter.deliveryMode'),
			this.translationService.getTR('filter.deliveryMode'),
			'deliveryMode',
			'deliveryModeDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500 })
		));

		map.set('serviceLevel', () => new DropdownFilter(
			'serviceLevel',
			this.translationService.getTR('datacenter-filter.service-level'),
			this.translationService.getTR('datacenter-filter.service-level'),
			'serviceLevel',
			'serviceLevelDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500 })
		));

		map.set('colorTopSide', () => new DropdownFilter(
			'colorTopSide',
			this.translationService.getTR('eoffer.table.header.colTopSide'),
			this.translationService.getTR('eoffer.table.header.colTopSide'),
			'colorTopSide',
			'colorTopSideDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500 })
		));

		map.set('colorBottomSide', () => new DropdownFilter(
			'colorBottomSide',
			this.translationService.getTR('datacenter-filter.color-bottom-side'),
			this.translationService.getTR('datacenter-filter.color-bottom-side'),
			'colorBottomSide',
			'colorBottomSideDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500 })
		));

		return map;
	};

	// eslint-disable-next-line @typescript-eslint/naming-convention
	CALLOFF_BY_VOLUME_FILTERS = () => {
		const map = this.columnsToFilters(this.defaultConfiguration.getAllColumnsOfDatacenter('calloff-by-volume'));

		map.set('soldTo', () => new SuggestiveListFilter(
			'soldTo',
			this.translationService.getTR('order.customer'),
			this.translationService.getTR('order.customer'),
			'soldTo',
			'soldToDescription',
			new FilterApiRequest(GET_DATACENTER_BATCHES_URL, { limit: 1500, distinct: true })
		));

		return map;
	};

	// eslint-disable-next-line @typescript-eslint/naming-convention
	CALLOFF_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'mill',
				() => new DropdownFilter(
					'mill',
					this.translationService.getTR('order.mill'),
					this.translationService.getTR('order.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					[],
					(_translation, code) => {
						if (!code) {
							throw new Error('Custom field expression for ecall mill expect the code');
						}
						const millConcatFilter: FieldExpression = {
							field: 'millConcat',
							term: `*${ term.escape(code) }*`,
							quoted: false
						};
						return millConcatFilter;
					}
				)
			],
			[
				'rowCreationDate',
				() => new DocumentDateFilter(
					'rowCreationDate',
					this.translationService.getTR('ecall-off-creation-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'rowCreationDate',
					'',
					'date'
				)
			],
			[
				'customerReference',
				() => new DatacenterFreeTextFilter(
					'customerReference',
					this.translationService.getTR('ecall-off.customer-call-off-reference'),
					this.translationService.getTR('ecall-off.customer-call-off-reference'),
					'customerReference'
				)
			],
			[
				'supplierOrderNumber',
				() => new DatacenterFreeTextFilter(
					'supplierOrderNumber',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('order.supplier.order.number'),
					'ecallVolumes.supplierOrderReference'
				)
			],
			[
				'batchNumber',
				() => new DatacenterFreeTextFilter(
					'batchNumber',
					this.translationService.getTR('commercial-documents.batch-number'),
					this.translationService.getTR('commercial-documents.batch-number'),
					'ecallBatches.batchReference'
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	QUALITY_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'customerReference',
				() => new DatacenterFreeTextFilter(
					'customerReference',
					this.translationService.getTR('claim.placeholder.reference.number'),
					this.translationService.getTR('claim.placeholder.reference.number'),
					'customerReference'
				)
			],
			[
				'supplierIssueNumber',
				() => new DatacenterFreeTextFilter(
					'supplierIssueNumber',
					this.translationService.getTR('issue.supplierissuenumber'),
					this.translationService.getTR('issue.supplierissuenumber'),
					'supplierIssueNumber'
				)
			],
			[
				'issueType',
				() => new DatacenterBooleanFilter(
					'issueType',
					this.translationService.getTR('claim.request.type'),
					[
						this.translationService.getTR('quapp.warning'),
						this.translationService.getTR('claim.quality.issue')
					],
					['issueType'],
					['warning', 'issue']
				)
			],
			[
				'claimType',
				() => new DropdownFilter(
					'claimType',
					this.translationService.getTR('claim.type.filter.label'),
					this.translationService.getTR('claim.type.filter.label'),
					'claimType',
					'claimType',
					new FilterApiRequest(CLAIM_SERVER_URL, {})
				)
			],
			[
				'creationDate',
				() => new DatacenterDateFilter(
					'creationDate',
					this.translationService.getTR('order.creation.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'creationDate',
					'date'
				)
			],
			[
				'soldTo',
				() => new SuggestiveListFilter(
					'soldTo',
					this.translationService.getTR('order.customer'),
					this.translationService.getTR('order.customer'),
					'soldTo',
					'soldToDescription',
					new FilterApiRequest(GET_CLAIM_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'shipTo',
				() => new SuggestiveListFilter(
					'shipTo',
					this.translationService.getTR('order.ship.to'),
					this.translationService.getTR('order.ship.to'),
					'shipTo',
					'shipToDescription',
					new FilterApiRequest(GET_CLAIM_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'comment',
				() => new DatacenterFreeTextFilter(
					'comment',
					this.translationService.getTR('claim.comment'),
					this.translationService.getTR('claim.comment'),
					'comment'
				)
			],
			[
				'customerOrderNumber',
				() => new OrderNumberFilter(
					'customerOrderNumber',
					this.translationService.getTR('order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderNumber',
					'customerOrderItemNumber'
				)
			],
			[
				'supplierOrderNumber',
				() => new OrderNumberFilter(
					'supplierOrderNumber',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderNumber',
					'supplierOrderItemNumber'
				)
			],
			[
				'status',
				() => new DatacenterBooleanFilter(
					'status',
					this.translationService.getTR('claim.current.status'),
					[
						this.translationService.getTR('issue.draft'),
						this.translationService.getTR('issue.inprocess'),
						this.translationService.getTR('claim.status.created'),
						this.translationService.getTR('global.cancelled'),
						this.translationService.getTR('quapp.process.closed.paid'),
						this.translationService.getTR('quapp.process.closed.refused'),
						this.translationService.getTR('quapp.process.closed.warning')
					],
					['status'],
					[
						'claimstatus.draft',
						'claimstatus.pending',
						'claimstatus.created',
						'claimstatus.cancelled',
						'claimstatus.paid',
						'claimstatus.refused',
						'claimstatus.registeredwarning'
					]
				)
			],
			[
				'closedDate',
				() => new DatacenterDateFilter(
					'closedDate',
					this.translationService.getTR('issue.closingdate'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'closedDate',
					'date'
				)
			],
			[
				'creditNoteDate',
				() => new DatacenterDateFilter(
					'creditNoteDate',
					this.translationService.getTR('issue.creditnotedate'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'creditNoteDate',
					'date'
				)
			],
			[
				'customerDefect',
				() => new DropdownFilter(
					'customerDefect',
					this.translationService.getTR('issue.customer.defect'),
					this.translationService.getTR('issue.customer.defect'),
					'customerDefectDetailed',
					'customerDefectDetailedDescription',
					new FilterApiRequest(GET_CLAIM_URL, { limit: 1500 })
				)
			],
			[
				'qtyAccepted',
				() => new DatacenterNumericFilter(
					'qtyAccepted',
					this.translationService.getTR('issue.acceptedquantity.value'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'qtyAccepted'
				)
			],
			[
				'qtyRequested',
				() => new DatacenterNumericFilter(
					'qtyRequested',
					this.translationService.getTR('issue.calimed.quantity.value'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'qtyRequested'
				)
			],
			[
				'amountRequested',
				() => new DatacenterNumericFilter(
					'amountRequested',
					this.translationService.getTR('issue.requested.total.amount.value'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'amountRequested'
				)
			],
			[
				'amountAccepted',
				() => new DatacenterFreeTextFilter(
					'amountAccepted',
					this.translationService.getTR('issue.total.amount.accepted.value'),
					this.translationService.getTR('issue.total.amount.accepted.value'),
					'amountAccepted'
				)
			],
			[
				'batches.batchReference',
				() => new DatacenterFreeTextFilter(
					'batches.batchReference',
					this.translationService.getTR('datacenter-filter.batch-reference'),
					this.translationService.getTR('datacenter-filter.batch-reference'),
					'batches.batchReference'
				)
			],
			[
				'origin',
				() => new DropdownFilter(
					'origin',
					this.translationService.getTR('issue.origin'),
					this.translationService.getTR('issue.origin'),
					'origin',
					'origin',
					new FilterApiRequest(GET_CLAIM_URL, { limit: 1500 })
				)
			],
			[
				'globalSearch',
				() => new GlobalSearchFilter(
					'globalSearch',
					this.translationService.getTR('global.search-filter'),
					this.translationService.getTR('global.search-filter'),
					[
						'customerReference',
						'supplierIssueNumber'
					]
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	QUALITY_DATACENTER_DEFAULT_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'customerReference',
				() => new DatacenterFreeTextFilter(
					'customerReference',
					this.translationService.getTR('claim.placeholder.reference.number'),
					this.translationService.getTR('claim.placeholder.reference.number'),
					'customerReference',
					['']
				)
			],
			[
				'supplierIssueNumber',
				() => new DatacenterFreeTextFilter(
					'supplierIssueNumber',
					this.translationService.getTR('issue.supplierissuenumber'),
					this.translationService.getTR('issue.supplierissuenumber'),
					'supplierIssueNumber',
					['']
				)
			],
			[
				'creationDate',
				() => new DatacenterDateFilter(
					'creationDate',
					this.translationService.getTR('order.creation.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'creationDate',
					'date',
					[{ from: '', to: '' }]
				)
			],
			[
				'status',
				() => new DatacenterBooleanFilter(
					'status',
					this.translationService.getTR('claim.current.status'),
					[
						this.translationService.getTR('issue.draft'),
						this.translationService.getTR('issue.inprocess'),
						this.translationService.getTR('claim.status.created'),
						this.translationService.getTR('global.cancelled'),
						this.translationService.getTR('quapp.process.closed.paid'),
						this.translationService.getTR('quapp.process.closed.refused'),
						this.translationService.getTR('quapp.process.closed.warning')
					],
					['status'],
					[
						'claimstatus.draft',
						'claimstatus.pending',
						'claimstatus.created',
						'claimstatus.cancelled',
						'claimstatus.paid',
						'claimstatus.refused',
						'claimstatus.registeredwarning'
					],
					[
						[
							{ apiName: 'status', value: 'claimstatus.draft', selected: false },
							{ apiName: 'status', value: 'claimstatus.pending', selected: false },
							{ apiName: 'status', value: 'claimstatus.created', selected: false },
							{ apiName: 'status', value: 'claimstatus.cancelled', selected: false },
							{ apiName: 'status', value: 'claimstatus.paid', selected: false },
							{ apiName: 'status', value: 'claimstatus.refused', selected: false },
							{ apiName: 'status', value: 'claimstatus.registeredwarning', selected: false },
						]
					]
				)
			],
			[
				'batches.batchReference',
				() => new DatacenterFreeTextFilter(
					'batches.batchReference',
					this.translationService.getTR('datacenter-filter.batch-reference'),
					this.translationService.getTR('datacenter-filter.batch-reference'),
					'batches.batchReference',
					['']
				)
			],
			[
				'issueType',
				() => new DatacenterBooleanFilter(
					'issueType',
					this.translationService.getTR('claim.request.type'),
					[
						this.translationService.getTR('claim.warning'),
						this.translationService.getTR('issue.claim')
					],
					['issueType'],
					[
						'claim.warning',
						'issue.claim'
					],
					[
						[
							{
								apiName: 'issueType',
								selected: true,
								value: 'warning'
							},
							{
								apiName: 'issueType',
								selected: true,
								value: 'issue'
							},
						]
					]
				)
			]
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	GUARANTEE_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'name',
				() => new DatacenterFreeTextFilter(
					'name',
					this.translationService.getTR('guarantee.card.client.project.name'),
					this.translationService.getTR('guarantee.card.client.project.name'),
					'name'
				)

			],
			[
				'status',
				() => new DatacenterBooleanFilter(
					'status',
					this.translationService.getTR('guarantee.current.status'),
					[
						this.translationService.getTR('guarantee.draft'),
						this.translationService.getTR('global.open'),
						this.translationService.getTR('issue.inprocess'),
						this.translationService.getTR('guarantee.satisfied'),
						this.translationService.getTR('guarantee.certificate.requested'),
						this.translationService.getTR('guarantee.certificate.issued'),
						this.translationService.getTR('guarantee.supplier.rejected'),
						this.translationService.getTR('guarantee.cancelled'),
					],
					['status'],
					[
						'Draft',
						'Open',
						'Launch analysis',
						'Satisfied',
						'Certificate requested',
						'Certificate issued',
						'Rejected by supplier',
						'Cancelled'
					]
				)
			]
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	ORGANIZATION_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'name',
				() => new DatacenterFreeTextFilter(
					'name',
					this.translationService.getTR('datacenter-filter.organization-name'),
					this.translationService.getTR('datacenter-filter.organization-name'),
					'name'
				)
			],
			[
				'service',
				() => new DropdownFilter(
					'service',
					this.translationService.getTR('datacenter-filter.organization-service'),
					this.translationService.getTR('datacenter-filter.organization-service'),
					'service.id',
					'service.labelTranslationId',
					new FilterApiRequest(GET_SERVICES, { limit: 150, distinct: false }),
					[],
					undefined,
					undefined,
					false,
					'diffusions.service.id',
					true,
					false
				)
			],
			[
				'role',
				() => new DropdownFilter(
					'role',
					this.translationService.getTR('datacenter-filter.organization-type'),
					this.translationService.getTR('datacenter-filter.organization-type'),
					'role',
					'role',
					new FilterApiRequest(GET_ORG_URL, { limit: 1500 })

				)
			],
			[
				'city',
				() => new DatacenterFreeTextFilter(
					'city',
					this.translationService.getTR('datacenter-filter.organization-city'),
					this.translationService.getTR('datacenter-filter.organization-city'),
					'address.city'
				)
			],
			[
				'country',
				() => new DropdownFilter(
					'country',
					this.translationService.getTR('organization.card.identity.country'),
					this.translationService.getTR('organization.card.identity.country'),
					'countryCode',
					'countryDescription',
					new FilterApiRequest(GET_COUNTRIES, { limit: 1500, distinct: true }),
					[],
					undefined,
					undefined,
					false,
					'address.countryCode',
					true
				)
			],
			[
				'bocClientName',
				() => new DatacenterFreeTextFilter(
					'bocClientName',
					this.translationService.getTR('datacenter-filter.back-office-customer'),
					this.translationService.getTR('datacenter-filter.back-office-customer'),
					'visibilityGroups.visibilityGroupBocClients.bocClient.name'
				)
			],
			[
				'bocRole',
				() => new DatacenterBooleanFilter(
					'bocRole',
					this.translationService.getTR('datacenter-filter.back-office-role'),
					['isPayer', 'isSoldTo', 'isShipTo'],
					['visibilityGroups.visibilityGroupBocClients'],
					['1', '1', '1']
				)
			],
			[
				'firstName',
				() => new DatacenterFreeTextFilter(
					'firstName',
					this.translationService.getTR('datacenter-filter.first-name'),
					this.translationService.getTR('datacenter-filter.first-name'),
					'personMembers.user.firstName'
				)
			],
			[
				'lastName',
				() => new DatacenterFreeTextFilter(
					'lastName',
					this.translationService.getTR('datacenter-filter.last-name'),
					this.translationService.getTR('datacenter-filter.last-name'),
					'personMembers.user.lastName'
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	USERS_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'firstName',
				() => new DatacenterFreeTextFilter(
					'firstName',
					this.translationService.getTR('datacenter-filter.first-name'),
					this.translationService.getTR('datacenter-filter.first-name'),
					'firstName'
				)
			],
			[
				'lastName',
				() => new DatacenterFreeTextFilter(
					'lastName',
					this.translationService.getTR('datacenter-filter.last-name'),
					this.translationService.getTR('datacenter-filter.last-name'),
					'lastName'
				)
			],
			[
				'email',
				() => new DatacenterFreeTextFilter(
					'email',
					this.translationService.getTR('datacenter-filter.user-email'),
					this.translationService.getTR('datacenter-filter.user-email'),
					'email'
				)
			],
			[
				'role',
				() => new DatacenterBooleanFilter(
					'role',
					this.translationService.getTR('datacenter-filter.user-role'),
					[
						this.translationService.getTR('add-suborg.administrator'),
						this.translationService.getTR('organizationrole.user'),
						this.translationService.getTR('organizationrole.hlpdk'),
						this.translationService.getTR('organizationrole.subscription'),
					],
					['role'],
					[
						'DSU',
						'user',
						'hlpdk',
						'subscription'
					]
				)
			],
			[
				'status',
				() => new DropdownFilter(
					'status',
					this.translationService.getTR('organization.user-status-in-organization'),
					this.translationService.getTR('organization.user-status-in-organization'),
					'status',
					'status',
					new FilterApiRequest(GET_SERVICES, { limit: 1500 }),
					[],
					createUsersStatusFilterExpression
				)
			],
			[
				'personStatus',
				() => new DropdownFilter(
					'personStatus',
					this.translationService.getTR('datacenter-filter.user-status'),
					this.translationService.getTR('datacenter-filter.user-status'),
					'personStatus',
					'personStatus',
					new FilterApiRequest(GET_SERVICES, { limit: 1500 })
				)
			],
			[
				'city',
				() => new DatacenterFreeTextFilter(
					'city',
					this.translationService.getTR('datacenter-filter.users-city'),
					this.translationService.getTR('datacenter-filter.users-city'),
					'city'
				)
			],
			[
				'countryCode',
				() => new DropdownFilter(
					'countryCode',
					this.translationService.getTR('organization.card.identity.country'),
					this.translationService.getTR('organization.card.identity.country'),
					'countryCode',
					'countryDescription',
					new FilterApiRequest(GET_COUNTRIES, { limit: 1500, distinct: true }),
					[],
					undefined,
					undefined,
					false,
					'countryCode',
					true
				)
			],
			[
				'companyName',
				() => new DatacenterFreeTextFilter(
					'companyName',
					this.translationService.getTR('datacenter-filter.organization-name'),
					this.translationService.getTR('datacenter-filter.organization-name'),
					'companyName'
				)
			],
			[
				'lastConnectionDate',
				() => new DatacenterDateFilter(
					'lastConnectionDate',
					this.translationService.getTR('settings.last-connection-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'lastConnectionDate',
					'date'
				)
			],
			[
				'servicesId',
				() => new DropdownFilter(
					'servicesId',
					this.translationService.getTR('user.notifications.dropdown.services'),
					this.translationService.getTR('user.notifications.dropdown.services'),
					'service.id',
					'service.labelTranslationId',
					new FilterApiRequest(
						GET_SERVICES,
						{
							limit: 1500,
							distinct: false
						}
					),
					[],
					undefined,
					undefined,
					true,
					'servicesId',
					true,
					false
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	AQDB_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'batchReference',
				() => new DatacenterFreeTextFilter(
					'batchReference',
					this.translationService.getTR('aqdb.batch'),
					this.translationService.getTR('aqdb.batch'),
					'batchReference'
				)
			],
			[
				'serialNumber',
				() => new DatacenterFreeTextFilter(
					'serialNumber',
					this.translationService.getTR('aqdb.serial.number'),
					this.translationService.getTR('aqdb.serial.number'),
					'serialNumber'
				)
			],
			[
				'recipient',
				() => new DatacenterFreeTextFilter(
					'recipient',
					this.translationService.getTR('aqdb.recipient'),
					this.translationService.getTR('aqdb.recipient'),
					'recipientLabel',
				)
			],
			[
				'customerArticleRef',
				() => new DatacenterFreeTextFilter(
					'customerArticleRef',
					this.translationService.getTR('aqdb.customer.article.ref'),
					this.translationService.getTR('aqdb.customer.article.ref'),
					'articleReference',
				)
			],
			[
				'dispatchDate',
				() => new DatacenterDateFilter(
					'dispatchDate',
					this.translationService.getTR('aqdb.dispatch.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'shipmentDate',
					'week'
				)
			],
			[
				'despatchAdviceNumber',
				() => new DatacenterFreeTextFilter(
					'despatchAdviceNumber',
					this.translationService.getTR('aqdb.N.of.dispatch.note'),
					this.translationService.getTR('aqdb.N.of.dispatch.note'),
					'despatchAdviceNumber',
				)
			],
			[
				'commercialQuality',
				() => new DatacenterFreeTextFilter(
					'commercialQuality',
					this.translationService.getTR('aqdb.commercial.quality'),
					this.translationService.getTR('aqdb.commercial.quality'),
					'commercialQualityLabel',
				)
			],
			[
				'deliveryMill',
				() => new DatacenterFreeTextFilter(
					'deliveryMill',
					this.translationService.getTR('aqdb.delivery.mill'),
					this.translationService.getTR('aqdb.delivery.mill'),
					'deliveryMillDescription',
				)
			],
			[
				'form',
				() => new DropdownFilter(
					'form',
					this.translationService.getTR('aqdb.form'),
					this.translationService.getTR('aqdb.form'),
					'form',
					'form',
					new FilterApiRequest(GET_AQDB_SERVER_URL, { limit: 1500 })
				)
			],
			[
				'grossWeight',
				() => new DatacenterNumericFilter(
					'grossWeight',
					this.translationService.getTR('aqdb.gross.weight'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'qtyBatchGross'
				)
			],
			[
				'realThickness',
				() => new DatacenterNumericFilter(
					'realThickness',
					this.translationService.getTR('aqdb.thickness.mm'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'realThickness'
				)
			],
			[
				'realWidth',
				() => new DatacenterNumericFilter(
					'realWidth',
					this.translationService.getTR('aqdb.width.mm'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'realWidth'
				)
			]
		]);
}

@Injectable({ providedIn: 'root' })
export class DatacenterFiltersConfiguration {
	private readonly filtersConfiguration: FiltersConfiguration;
	private readonly batchFiltersConfiguration: BatchFiltersConfiguration;
	private readonly orderFiltersConfiguration: OrderFiltersConfiguration;
	private readonly documentFiltersConfiguration: DocumentFiltersConfiguration;
	private readonly scheduleBusinessOrderFiltersConfiguration: ScheduleBusinessOrderFiltersConfiguration;
	private readonly shipmentFiltersConfiguration: ShipmentFiltersConfiguration;
	private readonly productListFiltersConfiguration: ProductListFiltersConfiguration;
	private readonly prototypeFiltersConfiguration: PrototypeFiltersConfiguration;

	constructor(
		readonly defaultConfiguration: DatacenterDefaultConfiguration,
		readonly translationService: TranslationService,
		readonly userService: UserService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {
		this.filtersConfiguration = new FiltersConfiguration(defaultConfiguration, translationService, userService);
		this.batchFiltersConfiguration = new BatchFiltersConfiguration(translationService);
		this.orderFiltersConfiguration = new OrderFiltersConfiguration(translationService);
		this.scheduleBusinessOrderFiltersConfiguration =
			new ScheduleBusinessOrderFiltersConfiguration(translationService);
		this.shipmentFiltersConfiguration = new ShipmentFiltersConfiguration(translationService);
		this.prototypeFiltersConfiguration = new PrototypeFiltersConfiguration(translationService);
		this.documentFiltersConfiguration = new DocumentFiltersConfiguration(translationService, userService);
		this.productListFiltersConfiguration = new ProductListFiltersConfiguration(
			translationService,
			productCharacteristicsTranslationService
		);
	}

	getFilter(filterId: string, datacenterType: DatacenterType) {
		let filterType;
		switch (datacenterType) {
		case 'calloff-by-batch':
			filterType = this.filtersConfiguration.CALLOFF_BY_BATCH_FILTERS().get(filterId);
			break;
		case 'calloff-by-volume':
			filterType = this.filtersConfiguration.CALLOFF_BY_VOLUME_FILTERS().get(filterId);
			break;
		case 'calloff':
			filterType = this.filtersConfiguration.CALLOFF_FILTERS().get(filterId);
			break;
		case 'order':
			filterType = this.orderFiltersConfiguration.ORDER_DATACENTER_FILTERS().get(filterId);
			break;
		case 'batch':
			filterType = this.batchFiltersConfiguration.BATCHES_DATACENTER_FILTERS().get(filterId);
			break;
		case 'shipment':
			filterType = this.shipmentFiltersConfiguration.SHIPMENTS_DATACENTER_FILTERS().get(filterId);
			break;
		case 'offer':
			break;
		case 'quality':
			filterType = this.filtersConfiguration.QUALITY_DATACENTER_FILTERS().get(filterId);
			break;
		case 'document':
			filterType = this.documentFiltersConfiguration.DOCUMENT_DATACENTER_FILTERS().get(filterId);
			break;
		case 'schedule-business-order':
			filterType = this.scheduleBusinessOrderFiltersConfiguration.SCHEDULE_BUSINESS_ORDER_DATACENTER_FILTERS()
				.get(filterId);
			break;
		case 'guarantee':
			filterType = this.filtersConfiguration.GUARANTEE_DATACENTER_FILTERS().get(filterId);
			break;
		case 'organization':
			filterType = this.filtersConfiguration.ORGANIZATION_DATACENTER_FILTERS().get(filterId);
			break;
		case 'users':
			filterType = this.filtersConfiguration.USERS_DATACENTER_FILTERS().get(filterId);
			break;
		case 'aqdb':
			filterType = this.filtersConfiguration.AQDB_DATACENTER_FILTERS().get(filterId);
			break;
		case 'prototypes':
			filterType = this.prototypeFiltersConfiguration.PROTOTYPE_DATACENTER_FILTERS().get(filterId);
			break;
		case 'product-list':
			filterType = this.productListFiltersConfiguration.PRODUCT_LIST_FILTERS().get(filterId);
			break;
		}
		if (!filterType) {
			console.warn(`Filter ${ filterId } not found for datacenter type ${ datacenterType }`);
			return undefined;
		}
		return filterType();
	}

	getDefaultFilter(datacenterType: DatacenterType) {
		switch (datacenterType) {
		case 'order':
			return Array.from(this.orderFiltersConfiguration.ORDER_DATACENTER_DEFAULT_FILTERS()
				.values()).map(value => value());
		case 'organization':
			return Array.from([]);
		case 'batch':
			return Array.from(this.batchFiltersConfiguration.BATCH_DATACENTER_DEFAULT_FILTERS()
				.values()).map(value => value());
		case 'shipment':
			return Array.from(this.shipmentFiltersConfiguration.SHIPMENTS_DATACENTER_DEFAULT_FILTERS()
				.values()).map(value => value());
		case 'offer':
			return Array.from([]);
		case 'quality':
			return Array.from(this.filtersConfiguration.QUALITY_DATACENTER_DEFAULT_FILTERS()
				.values()).map(value => value());
		case 'document':
			return Array.from(this.documentFiltersConfiguration.DOCUMENT_DATACENTER_DEFAULT_FILTERS()
				.values()).map(value => value());
		case 'schedule-business-order':
			return Array.from(this.scheduleBusinessOrderFiltersConfiguration
				.SCHEDULE_BUSINESS_ORDER_DEFAULT_DATACENTER_FILTERS().values()).map(value => value());
		case 'users':
			return Array.from([]);
		case 'prototypes':
			return Array.from([]);
		case 'product-list':
			return Array.from([]);
		case 'calloff-by-batch':
			return Array.from([]);
		case 'calloff-by-volume':
			return Array.from([]);
		case 'calloff':
			return Array.from([]);
		case 'guarantee':
			return Array.from([]);
		case 'aqdb':
			return Array.from([]);
		default:
			throw new Error(`Unsuported datacenter filters: ${ String(datacenterType) }`);
		}
	}

	getAllFilters(datacenterType: DatacenterType) {
		switch (datacenterType) {
		case 'calloff-by-batch':
			return Array.from(this.filtersConfiguration.CALLOFF_BY_BATCH_FILTERS().values()).map(value => value());
		case 'calloff-by-volume':
			return Array.from(this.filtersConfiguration.CALLOFF_BY_VOLUME_FILTERS().values()).map(value => value());
		case 'calloff':
			return Array.from(this.filtersConfiguration.CALLOFF_FILTERS().values()).map(value => value());
		case 'order':
			return Array.from(this.orderFiltersConfiguration.ORDER_DATACENTER_FILTERS().values())
				.map(value => value());
		case 'organization':
			return Array.from(this.filtersConfiguration.ORGANIZATION_DATACENTER_FILTERS().values())
				.map(value => value());
		case 'batch':
			return Array.from(this.batchFiltersConfiguration.BATCHES_DATACENTER_FILTERS()
				.values()).map(value => value());
		case 'shipment':
			return Array.from(this.shipmentFiltersConfiguration.SHIPMENTS_DATACENTER_FILTERS()
				.values()).map(value => value());
		case 'offer':
			return Array.from([]);
		case 'quality':
			return Array.from(this.filtersConfiguration.QUALITY_DATACENTER_FILTERS().values()).map(value => value());
		case 'document':
			return Array.from(this.documentFiltersConfiguration.DOCUMENT_DATACENTER_FILTERS()
				.values()).map(value => value());
		case 'schedule-business-order':
			return Array.from(this.scheduleBusinessOrderFiltersConfiguration
				.SCHEDULE_BUSINESS_ORDER_DATACENTER_FILTERS().values()).map(value => value());
		case 'guarantee':
			return Array.from(this.filtersConfiguration.GUARANTEE_DATACENTER_FILTERS().values()).map(value => value());
		case 'users':
			return Array.from(this.filtersConfiguration.USERS_DATACENTER_FILTERS().values())
				.map(value => value());
		case 'aqdb':
			return Array.from(this.filtersConfiguration.AQDB_DATACENTER_FILTERS().values()).map(value => value());
		case 'prototypes':
			return Array.from(this.prototypeFiltersConfiguration.PROTOTYPE_DATACENTER_FILTERS().values())
				.map(value => value());
		case 'product-list':
			return Array.from(this.productListFiltersConfiguration
				.PRODUCT_LIST_FILTERS().values()).map(value => value());
		default:
			throw new Error(`Unsuported datacenter filters: ${ String(datacenterType) }`);
		}
	}
}
